/** Audio Cleaner product-page settings & helpers (trim denoise uses separate task_type). */

export const AUDIO_CLEANER_PAGE_TASK_TYPE = 'audio-cleaner'
export const AUDIO_ENHANCER_PAGE_TASK_TYPE = 'audio-enhancer'
/** Product pages that share the audio-generation create/history APIs. */
export const AUDIO_PAGE_TASK_TYPES = [
  AUDIO_CLEANER_PAGE_TASK_TYPE,
  AUDIO_ENHANCER_PAGE_TASK_TYPE,
]
export const AUDIO_CLEANER_MAX_FILES = 5
/** Anonymous users may only queue a single file for enhance. */
export const AUDIO_CLEANER_ANON_MAX_FILES = 1
export const AUDIO_CLEANER_MAX_SIZE_BYTES = 300 * 1024 * 1024
export const AUDIO_CLEANER_MAX_SECONDS = 20 * 60
export const AUDIO_CLEANER_MIN_RECORD_SECONDS = 10
export const AUDIO_CLEANER_UPLOAD_ACCEPT = '.mp3,.wav,.aac,.m4a,.amr,audio/mpeg,audio/wav,audio/aac,audio/mp4,audio/amr'

/**
 * Logged-in billing: 1 credit per 60s of audio per task (ceil).
 * e.g. 1–60s → 1, 61–120s → 2.
 */
export function audioCleanerCreditsForDuration(durationSec) {
  const seconds = Math.max(0, Number(durationSec) || 0)
  if (seconds <= 0) return 0
  return Math.ceil(seconds / 60)
}
/** Right-panel empty-state Before / After demo (same-origin; CDN has no CORS). */
export const AUDIO_CLEANER_DEMO_BEFORE_URL =
  '/audio-cleaner/sample_unenhanced.mp3'
export const AUDIO_CLEANER_DEMO_AFTER_URL =
  '/audio-cleaner/sample_enhanced.mp3'
/** @deprecated Prefer before/after demo player; kept for reference assets. */
export const AUDIO_CLEANER_HISTORY_EMPTY_IMAGE =
  'https://static.lip-sync.ai/lipsync/audio-cleaner/history_empty.webp'

/** @type {{ key: string, labelKey: string, icon: string }[]} */
export const AUDIO_CLEANER_SETTING_OPTIONS = [
  { key: 'remove_background_audio', labelKey: 'optRemoveBackgroundAudio', icon: 'film' },
  { key: 'remove_echo', labelKey: 'optRemoveEcho', icon: 'microphone' },
  { key: 'muted', labelKey: 'optMuted', icon: 'pause' },
  { key: 'remove_noise', labelKey: 'optRemoveNoise', icon: 'auto-fix' },
  { key: 'long_silences', labelKey: 'optLongSilences', icon: 'alarm' },
  { key: 'mouth_sounds', labelKey: 'optMouthSounds', icon: 'microphone' },
  { key: 'stutters', labelKey: 'optStutters', icon: 'text-box-outline' },
  { key: 'fillers', labelKey: 'optFillers', icon: 'text-box-outline' },
  { key: 'hesitations', labelKey: 'optHesitations', icon: 'close' },
  { key: 'keep_music', labelKey: 'optKeepMusic', icon: 'film' },
  { key: 'autoeq', labelKey: 'optAutoeq', icon: 'cog' },
  { key: 'normalize', labelKey: 'optNormalize', icon: 'check' },
  { key: 'studio_sound', labelKey: 'optStudioSound', icon: 'auto-fix' },
  { key: 'remove_reverb', labelKey: 'optRemoveReverb', icon: 'microphone' },
]

export function createDefaultAudioCleanerSettings() {
  return Object.fromEntries(
    AUDIO_CLEANER_SETTING_OPTIONS.map(({ key }) => [key, false]),
  )
}

/**
 * Build create-task body. Only includes switch keys that are true.
 * @param {{
 *   audio_url: string,
 *   duration: number,
 *   settings?: Record<string, boolean>,
 *   task_type?: string,
 * }} params
 */
export function buildAudioCleanerCreatePayload({
  audio_url,
  duration,
  settings = {},
  task_type = AUDIO_CLEANER_PAGE_TASK_TYPE,
}) {
  const body = {
    generation_type: 'audio_cleaner',
    coin_type: 'gold_coin',
    task_type,
    audio_url,
    duration,
  }
  for (const { key } of AUDIO_CLEANER_SETTING_OPTIONS) {
    if (settings[key]) body[key] = true
  }
  return body
}

function fileExtension(name = '') {
  const base = String(name).split(/[/\\]/).pop() || ''
  const dot = base.lastIndexOf('.')
  return dot >= 0 ? base.slice(dot + 1).toLowerCase() : ''
}

const ALLOWED_EXTS = new Set(['mp3', 'wav', 'aac', 'm4a', 'amr'])
const ALLOWED_MIMES = new Set([
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/aac',
  'audio/x-aac',
  'audio/mp4',
  'audio/x-m4a',
  'audio/amr',
  'audio/amr-wb',
  'audio/3gpp',
])

export function isAudioCleanerInputFile(file) {
  if (!file) return false
  const ext = fileExtension(file.name)
  if (ext === 'flac') return false
  if (ALLOWED_EXTS.has(ext)) return true
  const mime = String(file.type || '').toLowerCase()
  if (ALLOWED_MIMES.has(mime)) return true
  if (mime.startsWith('audio/') && ext && ALLOWED_EXTS.has(ext)) return true
  return false
}

/** Normalize media duration to 2 decimal places (matches clock display). */
export function roundAudioDuration(seconds) {
  const n = Number(seconds)
  if (!Number.isFinite(n) || n <= 0) return 0
  return Math.round(n * 100) / 100
}

/** Format as mm:ss.SS (seconds to 2 decimal places). */
export function formatClock(seconds) {
  const totalCs = Math.max(0, Math.round((Number(seconds) || 0) * 100))
  const m = Math.floor(totalCs / 6000)
  const s = (totalCs % 6000) / 100
  return `${String(m).padStart(2, '0')}:${s.toFixed(2).padStart(5, '0')}`
}

export function basenameFromUrl(url) {
  try {
    const path = new URL(url).pathname
    const name = path.split('/').filter(Boolean).pop()
    return name ? decodeURIComponent(name) : 'audio'
  } catch {
    return 'audio'
  }
}

/** Strip a single trailing file extension (`song.mp3` → `song`). */
export function stripFileExtension(name) {
  const s = String(name || '').trim()
  if (!s) return ''
  return s.replace(/\.[^/.]+$/, '') || s
}

/** File extension including dot from a URL (`…/a.wav` → `.wav`). */
export function fileExtensionFromUrl(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''
  let base = ''
  try {
    const path = new URL(raw).pathname
    base = decodeURIComponent(path.split('/').filter(Boolean).pop() || '')
  } catch {
    const last = raw.split('/').pop() || ''
    base = decodeURIComponent(last.split('?')[0] || '')
  }
  const match = base.match(/(\.[^/.]+)$/)
  return match ? match[1] : ''
}

/** Display label from `params.audio_url` basename, without extension. */
export function displayNameFromAudioUrl(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''
  let base = ''
  try {
    const path = new URL(raw).pathname
    base = decodeURIComponent(path.split('/').filter(Boolean).pop() || '')
  } catch {
    const last = raw.split('/').pop() || ''
    base = decodeURIComponent(last.split('?')[0] || '')
  }
  return stripFileExtension(base)
}

/**
 * History / creations label: stem from input `audio_url`, extension from generated result URL.
 * e.g. audio_url `…/take.mp3` + result `…/out.wav` → `take.wav`
 */
export function audioResultDisplayName({ audioUrl, resultUrl, fallbackStem = 'audio' } = {}) {
  const stem =
    displayNameFromAudioUrl(audioUrl) ||
    stripFileExtension(fallbackStem) ||
    'audio'
  const ext = fileExtensionFromUrl(resultUrl)
  return ext ? `${stem}${ext}` : stem
}
